import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote, Star } from "lucide-react";
import { company } from "../data/company";
import { useGoogleReviews, normalizeGoogleReview } from "../hooks/useGoogleReviews";
import { trackClick } from "../utils/analytics";

export const ReviewsSection = ({ limit = 6, title = "Wat klanten zeggen", showAllLink = true, variant = "dark" }) => {
  const live = useGoogleReviews();

  // Uitsluitend live Google reviews — geen curated/mocked reviews meer.
  const liveItems = live.configured ? (live.reviews || []).map(normalizeGoogleReview) : [];
  const items = liveItems.slice(0, limit);

  const displayRating = live.configured && live.rating ? live.rating.toFixed(1) : null;
  const displayCount = live.configured && live.userRatingCount ? live.userRatingCount : null;
  const liveBadge = displayRating ? `Google · ${displayRating}` : "Google";

  const isElevated = variant === "elevated";

  return (
    <section
      className={`relative py-20 md:py-28 ${isElevated ? "" : "border-t border-koning-border"}`}
      style={isElevated ? { background: "radial-gradient(120% 80% at 50% 0%, #1A1A1A 0%, #0F0F0F 60%, #0A0A0A 100%)" } : undefined}
      data-testid="reviews-section"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-4" data-testid="reviews-rating-line">
              Reviews{displayRating ? ` — ${displayRating}/5` : ""}{displayCount ? ` · ${displayCount} Google-beoordelingen` : ""}
              {live.configured && <span className="ml-2 text-koning-gold">· live</span>}
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight max-w-2xl">
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-6 text-xs tracking-[0.2em] uppercase text-koning-muted">
            {live.configured && live.googleMapsUri ? (
              <a href={live.googleMapsUri} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="reviews-google-link">
                <span className="text-koning-gold font-display text-2xl leading-none">G</span>
                <span>{liveBadge}</span>
              </a>
            ) : (
              <span className="flex items-center gap-2">
                <span className="text-koning-gold font-display text-2xl leading-none">G</span>
                <span>{liveBadge}</span>
              </span>
            )}
            {/* Werkspot: badge + profiel-link zonder review-aantal totdat het actuele
                aantal door de klant is bevestigd. Google-aantal wordt niet opgeteld. */}
            <a
              href={company.werkspot?.profileUrl || "https://www.werkspot.nl"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-white transition-colors"
              data-testid="reviews-werkspot-link"
            >
              <span className="text-koning-gold font-display text-2xl leading-none">W</span>
              <span>Werkspot Pro</span>
            </a>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((r) => (
            <article
              key={r.id}
              className={`p-7 flex flex-col rounded-[2px] ${
                isElevated
                  ? "bg-[#161616] border border-koning-gold/15 review-card-premium"
                  : "bg-koning-surface border border-koning-border hover:border-koning-gold/40 transition-all"
              }`}
              data-testid={`review-${r.id}`}
            >
              <Quote className="text-koning-gold mb-5" size={28} />
              <p className="text-koning-text text-sm leading-relaxed flex-1">{r.text}</p>
              <div className={`mt-6 pt-5 border-t ${isElevated ? "border-koning-gold/10" : "border-koning-border"} flex items-center justify-between`}>
                <div>
                  <div className="text-white text-sm font-medium">{r.name}</div>
                  <div className="text-koning-muted text-xs mt-0.5">
                    {[r.city, r.project, r.date].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-0.5 justify-end">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} size={12} className="fill-koning-gold text-koning-gold" />
                    ))}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-koning-muted mt-1">{r.source}</div>
                </div>
              </div>
            </article>
            ))}
          </div>
        ) : (
          <p className="text-koning-muted leading-relaxed" data-testid="reviews-section-fallback">
            Onze Google-reviews worden live opgehaald en verschijnen hier automatisch. Bekijk alle beoordelingen via de knoppen hierboven of op onze reviewpagina.
          </p>
        )}

        {showAllLink && (
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-koning-border pt-10">
            <p className="text-koning-muted text-sm max-w-xl">
              {live.configured
                ? "Onze Google reviews worden elke 24 uur automatisch live opgehaald. Werkspot reviews voegen wij periodiek toe."
                : "Wij koppelen onze Google en Werkspot reviews binnenkort live aan deze pagina — alle reviews zijn echt en herleidbaar."}
            </p>
            <Link to="/reviews" className="btn-outline" data-testid="reviews-all-link">
              Alle reviews bekijken <ArrowUpRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export const StatsBar = ({ variant = "dark" }) => {
  const lightCls = variant === "light" ? "section-light" : "bg-koning-surface";
  const valueColor = variant === "light" ? "text-[#8A7430]" : "text-koning-gold";
  return (
    <section className={`py-16 border-y ${variant === "light" ? "border-[#E1D9C5]" : "border-koning-border"} ${lightCls}`} data-testid="stats-bar">
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-3 gap-10">
        {company.stats.map((s, i) => (
          <div key={i}>
            <div className={`font-display ${valueColor} text-4xl md:text-5xl tracking-tight`}>{s.value}</div>
            <div className={`text-[11px] tracking-[0.24em] uppercase mt-2 ${variant === "light" ? "text-[#6F6A60]" : "text-koning-muted"}`}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const WhyChoose = ({ items, variant = "dark" }) => {
  if (variant === "light") {
    // Full-width light premium variant — clean two-column layout, no split contrast
    return (
      <section className="section-light py-20 md:py-28 border-y border-[#E1D9C5]" data-testid="why-choose">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-24 items-start">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Waarom Koning</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1816]">
              Eén partij. <br />
              <span className="text-[#8A7430] italic">Vaste prijs.</span> <br />
              Geen verrassingen.
            </h2>
            <p className="mt-6 leading-relaxed max-w-md text-[#4A453E]">
              Wij zijn aannemer van A tot Z. Eén aanspreekpunt, een vast uitvoeringsteam en strak projectmanagement. Premium kwaliteit zonder de chaos van losse partijen.
            </p>
          </div>
          <ul className="space-y-5">
            {items.map((t, i) => (
              <li key={i} className="flex items-start gap-5 border-b border-[#E1D9C5] pb-5 last:border-0">
                <div className="font-display text-[#8A7430] text-2xl leading-none w-10 flex-shrink-0">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-[#1A1816] text-base md:text-lg leading-snug">{t}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  if (variant === "split") {
    // Left = light cream panel, Right = dark panel — premium asymmetric layout
    return (
      <section className="grid lg:grid-cols-2 border-y border-koning-border" data-testid="why-choose">
        <div className="section-light px-6 md:px-12 lg:px-20 py-20 md:py-28 flex flex-col justify-center">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Waarom Koning</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1816]">
            Eén partij. <br />
            <span className="text-[#8A7430] italic">Vaste prijs.</span> <br />
            Geen verrassingen.
          </h2>
          <p className="mt-6 leading-relaxed max-w-md text-[#4A453E]">
            Wij zijn aannemer van A tot Z. Eén aanspreekpunt, een vast uitvoeringsteam en strak projectmanagement. Premium kwaliteit zonder de chaos van losse partijen.
          </p>
        </div>
        <div className="bg-[#0B0B0B] px-6 md:px-12 lg:px-16 py-20 md:py-28 flex items-center">
          <ul className="space-y-5 w-full">
            {items.map((t, i) => (
              <li key={i} className="flex items-start gap-4 border-b border-koning-border pb-5 last:border-0">
                <div className="font-display text-koning-gold text-2xl leading-none w-10">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-white text-base md:text-lg">{t}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28" data-testid="why-choose">
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <div className="eyebrow mb-4">Waarom Koning</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight">
            Eén partij. <br />
            <span className="text-koning-gold">Vaste prijs.</span> <br />
            Geen verrassingen.
          </h2>
          <p className="mt-6 text-koning-muted leading-relaxed max-w-md">
            Wij zijn aannemer van A tot Z. Eén aanspreekpunt, een vast uitvoeringsteam en strak projectmanagement. Premium kwaliteit zonder de chaos van losse partijen.
          </p>
        </div>
        <ul className="space-y-5">
          {items.map((t, i) => (
            <li key={i} className="flex items-start gap-4 border-b border-koning-border pb-5 last:border-0">
              <div className="font-display text-koning-gold text-2xl leading-none w-10">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-white text-base md:text-lg">{t}</div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export const ProcessSection = ({ items, variant = "dark" }) => {
  if (variant === "light") {
    return (
      <section className="section-light py-20 md:py-28 border-y border-[#E1D9C5]" data-testid="process-section">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Onze werkwijze</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1816]">Van eerste gesprek tot oplevering</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E1D9C5]">
            {items.map((p) => (
              <div key={p.step} className="bg-white p-8 lg:p-10 shadow-premium">
                <div className="font-display text-[#8A7430] text-5xl tracking-tight">{p.step}</div>
                <h3 className="font-display text-[#1A1816] text-xl mt-4">{p.title}</h3>
                <p className="text-[#6F6A60] text-sm leading-relaxed mt-3">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="py-20 md:py-28 bg-koning-surface border-y border-koning-border" data-testid="process-section">
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-4">Onze werkwijze</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight">Van eerste gesprek tot oplevering</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-koning-border">
          {items.map((p) => (
            <div key={p.step} className="bg-koning-surface p-8 lg:p-10">
              <div className="font-display text-koning-gold text-5xl tracking-tight">{p.step}</div>
              <h3 className="font-display text-white text-xl mt-4">{p.title}</h3>
              <p className="text-koning-muted text-sm leading-relaxed mt-3">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CtaStrip = ({ title, subtitle, variant = "dark" }) => {
  // Both variants now render the modern navy card with orange CTA
  return (
    <section className="py-16 md:py-20" data-testid="cta-strip">
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="rounded-[28px] p-10 md:p-14 lg:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8 relative overflow-hidden" style={{ background: "#08182A" }}>
          <div className="relative z-10">
            <div className="text-xs font-semibold tracking-[0.18em] uppercase mb-3" style={{ color: "#B8924A" }}>Volgende stap</div>
            <h3 className="text-2xl md:text-4xl text-white tracking-tight font-bold max-w-2xl">{title}</h3>
            {subtitle && <p className="mt-3 text-white/80 max-w-2xl leading-relaxed">{subtitle}</p>}
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/offerte"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold whitespace-nowrap transition-all hover:translate-y-[-2px]"
              style={{ background: "#B8924A", boxShadow: "0 4px 16px -2px rgba(184, 146, 74, 0.4)" }}
              data-testid="cta-strip-offerte"
            >
              Offerte aanvragen <ArrowUpRight size={16} />
            </Link>
            <a
              href={company.phoneHref}
              onClick={() => trackClick("phone", "cta-strip")}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold border-2 border-white/30 hover:bg-white/10 transition-all whitespace-nowrap"
              data-testid="cta-strip-call"
            >
              Bel {company.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
